const CheckoutError = require('@/lib/Checkout/CheckoutError');
const CheckoutErrorActions = require('@/lib/Checkout/CheckoutErrorActions');
const CheckoutResult = require('@/lib/Checkout/CheckoutResult');
const CheckoutStage = require('@/lib/Checkout/CheckoutStage');
const CheckoutType = require('@/lib/Checkout/CheckoutType');
const CustomerScreenStages = require('@/lib/CustomerScreen/CustomerScreenStages');
const EventNames = require('@/lib/Events/EventNames');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const OnlineTaskTrigger = require('@/lib/OnlineQueue/OnlineTaskTrigger');
const OrderRequest = require('@/lib/Orders/OrderRequest');
const PaymentCtrl = require('@/controllers/modal/PaymentCtrl');
const PaymentSaveError = require('@/lib/Payments/PaymentSaveError');
const qs = require('qs');
const UserCancelledPaymentError = require('@/lib/Checkout/PaymentCancelledError');
const { clearLoadingMessage, setLoadingMessage } = require('@/functions/ui');
const { container } = require('@/container');
const { currencyFilter } = require('@/vue/filters/currencyFilter');
const { MODAL_NAMES__EPOS } = require('@/epos/vue/modals/MODAL_NAMES__EPOS');
const { sanitizeLoggedObject } = require('@/lib/Logging');

/**
 * @typedef {object} CheckoutOptions
 * @property {boolean} [forceOffline]
 * @property {boolean} [skipPayment]
 */

class CheckoutService {
    /**
     * @param {ApiClient} apiClient
     * @param {CartService} cartService
     * @param {CartToConfirmationStructureMapper} cartToConfirmationStructureMapper
     * @param {CartToOrderMapper} cartToOrderMapper
     * @param {CartToReservationStructureMapper} cartToReservationStructureMapper
     * @param {ConnectivityChecker} connectivityChecker
     * @param {CurrentDevice} currentDevice
     * @param {CustomerScreenDataService} customerScreenDataService
     * @param {LangService} langService
     * @param {MembershipService} membershipService
     * @param {ModalFactory} modalFactory
     * @param {OrderAdjustmentConfirmer} orderAdjustmentConfirmer
     * @param {OrderConfirmer} orderConfirmer
     * @param {OrderInProgressStasher} orderInProgressStasher
     * @param {OrderQueue} orderQueue
     * @param {OrdersApi} ordersApi
     * @param {OrderService} orderService
     * @param {PaymentMethodService} paymentMethodService
     * @param {PaymentService} paymentService
     * @param {ToastFactory} toastFactory
     * @param {UserService} userService
     * @param {VueModalFactory} vueModalFactory
     */
    constructor(
        apiClient,
        cartService,
        cartToConfirmationStructureMapper,
        cartToOrderMapper,
        cartToReservationStructureMapper,
        connectivityChecker,
        currentDevice,
        customerScreenDataService,
        langService,
        membershipService,
        modalFactory,
        orderAdjustmentConfirmer,
        orderConfirmer,
        orderInProgressStasher,
        orderQueue,
        ordersApi,
        orderService,
        paymentMethodService,
        paymentService,
        toastFactory,
        userService,
        vueModalFactory,
    ) {
        this.apiClient = apiClient;
        this.cartService = cartService;
        this.cartToConfirmationStructureMapper = cartToConfirmationStructureMapper;
        this.cartToOrderMapper = cartToOrderMapper;
        this.cartToReservationStructureMapper = cartToReservationStructureMapper;
        this.connectivityChecker = connectivityChecker;
        this.currentDevice = currentDevice;
        this.customerScreenDataService = customerScreenDataService;
        this.langService = langService;
        this.membershipService = membershipService;
        this.modalFactory = modalFactory;
        this.orderAdjustmentConfirmer = orderAdjustmentConfirmer;
        this.orderConfirmer = orderConfirmer;
        this.orderInProgressStasher = orderInProgressStasher;
        this.orderQueue = orderQueue;
        this.ordersApi = ordersApi;
        this.orderService = orderService;
        this.paymentMethodService = paymentMethodService;
        this.paymentService = paymentService;
        this.toastFactory = toastFactory;
        this.userService = userService;
        this.vueModalFactory = vueModalFactory;

        /**
         * @type {ConsoleLogger}
         */
        this.log = logFactory('CheckoutService');
    }

    /**
     * @param {CheckoutOptions} [options]
     *
     * @returns {Promise<CheckoutResult>}
     * @throws {CheckoutError}
     */
    async checkout(
        options = {},
    ) {
        this.closeErrorModals();

        // Ensure a payment method is available (if required).
        await this.ensurePaymentMethodAvailable();

        /**
         * Which type of checkout are we performing?
         *
         * @type {string}
         * @see {CheckoutType}
         */
        let checkoutType;

        if (this.cartService.currentCart.isOrderAdjustmentBalancePayment()) {
            checkoutType = CheckoutType.BALANCE_PAYMENT;
        } else if (this.cartService.currentCart.isEditingOrder) {
            checkoutType = CheckoutType.ADJUSTMENT;
        } else {
            // Default to the confirmed order flow.
            checkoutType = CheckoutType.NEW_ORDER__CONFIRMED;
        }

        // Determine if we are going to create an order online or offline.
        // Previously we'd try and creating an order and if it failed we'd assume we were offline. Now we'll check it
        // here and handle it better.
        //
        // With an unreliable connection there's a chance this says we're online but then fails when we try to create
        // the order. It can be a long time between this check and saving the order, because the user takes payment
        // as part of this process. In that case the user will see the order error modal. But there can be a "store
        // order for later processing" button in there to handle that.
        //
        // In https://digitickets.atlassian.net/browse/PROP-218 we will make an option to force a device into
        // offline mode. That will handle the case of an unreliable connection. The user should manually switch to
        // offline mode.
        let isOnlineCheckout;
        if (options?.forceOffline) {
            this.log.debug('Forcing offline checkout due to supplied options.');
            isOnlineCheckout = false;
        } else {
            isOnlineCheckout = await this.checkConnectivity();
        }

        if (checkoutType === CheckoutType.NEW_ORDER__CONFIRMED) {
            if (!isOnlineCheckout) {
                this.log.debug('Checking out while offline. Determined before payment.');
                checkoutType = CheckoutType.NEW_ORDER__OFFLINE;
            } else if (this.useUnconfirmedOrderFlow()) {
                checkoutType = CheckoutType.NEW_ORDER__UNCONFIRMED;
            }
        }

        this.log.debug(`Checkout type: ${checkoutType}`);

        // These variables are populated with the result of the confirmation step.
        let order;
        let orderAdjustmentResponse;
        let orderSaveError;
        let orderConfirmStageName;
        let paymentSaveError;

        // It's possible that the checkout is being called again after an order
        // has already been successfully created. This can happen if a payment failed to save,
        // and the user has pressed the retry button in the payment error modal.
        const existingCheckoutResult = this.cartService.currentCart.checkoutResult;
        if (existingCheckoutResult && existingCheckoutResult.order?.ID) {
            order = existingCheckoutResult.order;
            this.log.debug('Using existing order from existing checkout result', order);
        }
        if (existingCheckoutResult && existingCheckoutResult.orderAdjustmentResponse) {
            orderAdjustmentResponse = existingCheckoutResult.orderAdjustmentResponse;
            this.log.debug('Using existing order adjustment response from existing checkout result', orderAdjustmentResponse);
        }

        if (!order) {
            // Create a reservation (if required).
            await this.createReservation();

            if (checkoutType === CheckoutType.NEW_ORDER__UNCONFIRMED) {
                // Create an unconfirmed order before taking payment.
                await this.createNewUnconfirmedOrderOnline();
            } else if (checkoutType === CheckoutType.NEW_ORDER__CONFIRMED) {
                // Do a dry-run of creating an order (but don't actually create one),
                // to check that the order is valid before taking payment.
                await this.validateOrder();
            }
        }

        // Show the payment modal so the user can take payments / issue refunds (if required).
        try {
            if (options?.skipPayment) {
                this.log.debug('Skipping payment due to supplied options.');
            } else {
                // This await can take a while in real-world time, because here we are waiting for the user
                // to collect payment from the customer.
                await this.takePayment(checkoutType);
            }

            // IMPORTANT: shouldSkipConnectivityCheckAfterPayment() checks for the existence of a test string in the
            // notes field to allow us to test network connectivity issues.
            if (!this.shouldSkipConnectivityCheckAfterPayment()) {
                // Check connectivity again after taking payment because it could have been a while.
                // This is important to force an error to happen if the user is trying to edit and order
                // or use the unconfirmed order flow while offline.
                // At this point we only switch TO offline mode but not FROM offline back to online, because it's too
                // late for that - we needed to take a reservation before taking payment for that to work.
                let isOnlineAfterPayment = await this.checkConnectivity();
                if (!isOnlineAfterPayment) {
                    this.log.debug('Checking out while offline. Determined after payment.');
                    checkoutType = CheckoutType.NEW_ORDER__OFFLINE;
                }
            }
        } catch (err) {
            if (this.cartService.currentCart.unconfirmedOrderID) {
                // With the unconfirmed order flow an order has already been created at this point.
                // If the user cancels the payment, we need to cancel that order.
                await this.cancelUnconfirmedOrder();
            }
            throw err;
        }

        // Now we should have:
        // - A cart that has been validated.
        // - A reservation, or an unconfirmed order.
        // - All desired payments taken and added to the cart.
        // Now we can adjust/confirm/create the order.

        await this.orderInProgressStasher.setState('sell');
        await this.cartService.clearTransactionInProgress();
        this.cartService.currentCart.balancePaid = true;

        if (!order) {
            try {
                // Perform the appropriate action to create or confirm the order depending
                // on which type of checkout this is.
                // Because we may be re-checking out after a failed payment, we may already have an order.
                // So use a little hack in the switch to only match the checkoutType when appropriate.
                switch (checkoutType) {
                    case CheckoutType.BALANCE_PAYMENT:
                        // 1. Create an order for the balance payment.
                        orderConfirmStageName = CheckoutStage.SAVE_PAYMENTS;
                        console.log('Saving balance payments.');
                        // We don't need to do anything here. Just use the original order.
                        order = this.cartService.currentCart.originalOrder;
                        break;

                    case CheckoutType.ADJUSTMENT:
                        // 2. Create the order adjustment.
                        orderConfirmStageName = CheckoutStage.CONFIRM_ADJUSTMENT;
                        console.log('Saving order adjustment.');
                        orderAdjustmentResponse = await this.createAdjustmentOrder();
                        order = orderAdjustmentResponse.adjustedOrder;
                        break;

                    case CheckoutType.NEW_ORDER__UNCONFIRMED:
                        // 3. Confirm the unconfirmed order that was created before taking payment.
                        orderConfirmStageName = CheckoutStage.CONFIRM_ORDER;
                        console.log('Confirming unconfirmed order.');
                        order = await this.confirmOrder();
                        break;

                    case CheckoutType.NEW_ORDER__CONFIRMED:
                        // 4. Create a confirmed new order online.
                        orderConfirmStageName = CheckoutStage.CREATE_CONFIRMED_ORDER;
                        console.log('Saving confirmed order online.');
                        order = await this.createNewConfirmedOrderOnline();
                        break;

                    case CheckoutType.NEW_ORDER__OFFLINE:
                        // 5. Create a new unconfirmed order offline, or confirm an existing unconfirmed order
                        // that previously failed due to a network issue.
                        orderConfirmStageName = CheckoutStage.CREATE_CONFIRMED_ORDER;
                        // Save the order to the queue.
                        await this.storeOrderForLaterProcessing();
                        // Create an order from the cart contents instead of the API response.
                        order = await this.createOrderFromCartForOffline();
                        break;
                }

                // Order was successfully created/confirmed this attempt.
                // Resolve any unresolved order errors left over from previous failed attempts.
                await this.cartService.resolveOrderSaveErrors();
            } catch (err) {
                // There was an error adjusting/confirming/creating the order.
                // We need to deal with this, but also want to continue to save payments against the order if possible.
                // So hold on to this error, attempt to save payments anyway, and then deal with the error afterward.
                orderSaveError = err;

                // Log the error.
                let orderErrorID;
                try {
                    orderErrorID = await this.logOrderError(
                        orderConfirmStageName,
                        this.cartService.currentCart,
                        orderSaveError,
                    );
                    orderSaveError.errorID = orderErrorID;
                } catch (logErr) {
                    // Swallow errors logging the errors.
                    console.error('There was an error logging the order save error.', logErr);
                }

                // Save the error to the cart. This will then be displayed in the order error modal,
                // and will also be restored if the app is restarted.
                // Note that we maintain a list of errors that occurred, not just the most recent.
                await this.cartService.modifyCurrentCart((cart) => {
                    cart.addOrderSaveError(orderSaveError);
                });

                // Produce an order based on the current cart.
                order = this.cartToOrderMapper.buildOrderFromApiConfirmation(
                    // TODO: https://digitickets.atlassian.net/browse/PROP-540
                    this.cartService,
                    null,
                );

                // With the confirmed order flow an order may have been created, to which we can add payments,
                // but we don't know the ID of that order due to the error.
                // We can look it up by thirdPartyRef to see if it exists.
                try {
                    const recoveredOrder = await this.ordersApi.findByThirdPartyRef(
                        this.cartService.currentCart.thirdPartyRef,
                    );
                    if (recoveredOrder) {
                        // Note that the 'order' we work with is the original order,
                        // so set the appropriate ID and bookingRef.
                        order.ID = recoveredOrder.originalOrderID || recoveredOrder.ID;
                        order.bookingRef = recoveredOrder.originalRef;
                        order.originalRef = recoveredOrder.originalRef;
                    }
                } catch {
                    // No saved order found with the thirdPartyRef. Guess it didn't make it!
                }
            }
        }

        const checkoutResult = new CheckoutResult(
            checkoutType,
            order,
            orderAdjustmentResponse
        );

        // Get all the new payments from the cart that we need to save.
        let newPaymentsExcludingGoCardless = this.paymentService.getNewPaymentsFromCartExcludingGoCardless(
            this.cartService.currentCart
        );
        let newGoCardlessPayments = this.paymentService.getNewGoCardlessPaymentsFromCart(
            this.cartService.currentCart
        );
        let paymentsToSave = [...newPaymentsExcludingGoCardless];

        if (checkoutType === CheckoutType.NEW_ORDER__CONFIRMED) {
            // TODO: https://digitickets.atlassian.net/browse/PROP-2503
            // We have reverted to sending payments with the order creation request instead of sending them separately,
            // because the "store payments" API endpoint is not doing its job.
            // So for now this is set to an empty array and payments are added to the order confirmation structure
            // in CartToConfirmationStructureMapper.
            // This only applies to new orders using the default confirmed order flow.
            paymentsToSave = [];
        }

        checkoutResult.setNewPayments([
            // Return all the new payments in the result. Both GC and not GC.
            ...newPaymentsExcludingGoCardless,
            ...newGoCardlessPayments,
        ]);

        // Store the checkout result in the current cart so that it gets persisted to the user's session.
        // By doing this we can restore the checkout result if the app is restarted.
        await this.cartService.modifyCurrentCart((currentCart) => {
            currentCart.checkoutResult = checkoutResult;
        });

        // Now an order has been created and confirmed. Add any new payments to it.
        // Note that we always store payments against the original order ID even for adjustment orders.
        if (order?.ID) {
            // Save all the payments from the cart against the order.
            // This returns the complete list of payments for the order (not just the newly saved payments), so we
            // can set that full list on the order.
            // This may return null if there were no new payments to save.
            try {
                const allOrderPayments = await this.addPaymentsToOrder(
                    paymentsToSave,
                    order,
                );
                if (allOrderPayments !== null) {
                    // If we got a list of payments back, set them on the order.
                    order.payments = allOrderPayments;
                }

                // Payments saved successfully. Resolve any unresolved payment errors from previous attempts.
                await this.cartService.resolvePaymentSaveErrors();
            } catch (err) {
                // There was an error saving payment(s).
                // Catch it, remember it, and display it in a sec.
                paymentSaveError = err;

                let paymentErrorID;
                try {
                    paymentErrorID = await this.logOrderError(
                        CheckoutStage.SAVE_PAYMENTS,
                        this.cartService.currentCart,
                        paymentSaveError,
                    );
                    paymentSaveError.errorID = paymentErrorID;
                } catch (logErr) {
                    // Swallow errors logging the errors.
                    console.error('There was an error logging the payment save error.', logErr);
                }

                // Save the error to the cart.
                await this.cartService.modifyCurrentCart((cart) => {
                    cart.addPaymentSaveError(paymentSaveError);
                });
            }
        }

        const hasOrderErrors = this.cartService.currentCart.hasUnresolvedOrderSaveErrors();
        const hasPaymentErrors = this.cartService.currentCart.hasUnresolvedPaymentSaveErrors();
        if (hasOrderErrors || hasPaymentErrors) {
            // Show the appropriate error modal(s) for any errors that occurred.
            // This happens asynchronously - we display the modal, and then meanwhile continue running this method.
            // Below it will throw an exception to stop further execution of the checkout process, and we don't
            // want the error modal display to block that.
            this.displayErrorModals(
                this.cartService.currentCart,
                order
            )
                .then(() => {})
                .catch((err) => {
                    console.error('Error displaying order/payment error modal.', err);
                });
        }

        if (orderSaveError) {
            // Throw an error to stop execution.
            // We don't set a message so that it is not shown to the user. The user will see the order error modal.
            throw new CheckoutError(
                orderConfirmStageName,
                '',
                orderSaveError,
            );
        } else if (paymentSaveError) {
            // Throw an error to stop execution.
            // We don't set a message so that it is not shown to the user. The user will see the payment error modal.
            throw new CheckoutError(
                CheckoutStage.SAVE_PAYMENTS,
                '',
                paymentSaveError,
            );
        }

        await this.updateRenewingMembershipsData();

        // Perform the standard post-confirmation actions (such as creating an invoice or credit note).
        // The OrderConfirmer says what needs to happen.
        if (checkoutResult.orderAdjustmentResponse) {
            await this.orderConfirmer.postAdjustmentConfirm(
                checkoutResult.orderAdjustmentResponse,
                checkoutResult.order
            );
        } else {
            await this.orderConfirmer.postConfirm(checkoutResult.order);
        }

        return checkoutResult;
    }

    /**
     * @returns {Promise<boolean>}
     */
    async checkConnectivity() {
        // Make a request to check if we're online (don't use the cached isOnline() value.)
        this.setLoadingMessage(CheckoutStage.CHECK_CONNECTIVITY);
        const isOnline = await this.connectivityChecker.check();
        this.clearLoadingMessage();

        if (!isOnline) {
            if (this.cartService.currentCart.isEditingOrder) {
                // All adjustments must happen online.
                throw new CheckoutError(
                    CheckoutStage.CHECK_CONNECTIVITY,
                    this.langService.getText('CHECKOUT_ERROR_MESSAGES.DEVICE_MUST_BE_ONLINE__ADJUSTMENTS'),
                );
            }

            if (this.currentDevice.device.forceOrdersOnline) {
                // The current device says it must be online to process orders, so fail.
                // This will cause an error to be shown. The user can press Pay again to try again.
                throw new CheckoutError(
                    CheckoutStage.CHECK_CONNECTIVITY,
                    this.langService.getText('CHECKOUT_ERROR_MESSAGES.DEVICE_MUST_BE_ONLINE__ORDERS'),
                );
            }
        }

        return isOnline;
    }

    /**
     * Create a reservation for the current cart.
     *
     * @returns {Promise}
     */
    async createReservation() {
        if (!this.cartService.currentCart.requiresReservation()) {
            // Not all orders require a reservation, for example a balance payment or a refund via order editing.
            this.log.debug('Skipping reservation because no reservation is required.');
            return;
        }

        if (!this.connectivityChecker.isOnline()) {
            // The device is offline, so we can't make a reservation.
            // We'll use a dummy reservation so the following code knows we're offline.
            this.log.debug('Skipping reservation because the device is offline.');
            return;
        }

        if (
            this.cartService.currentCart.reservation
            && this.cartService.currentCart.reservation.expiresAt > new Date()
        ) {
            // The cart already has a reservation.
            // This can happen when reattempting to send an order that failed.
            // TODO: Check if that reservation is expired.
            // Maybe an API call to reconfirm the reservation and bump its time?
            // Even if the reservation isn't expired yet there is a risk its now on the verge of expiring.
            this.log.debug(
                'Skipping reservation because a reservation already exists.',
                this.cartService.currentCart.reservation,
            );
            return;
        }

        try {
            this.log.debug('Creating reservation.');
            this.setLoadingMessage(CheckoutStage.RESERVE_ITEMS);

            const reservation = await this.cartService.createReservation();

            this.log.debug('Successfully created reservation.', reservation);
        } catch (err) {
            this.log.error('Error creating reservation.', err);

            // Creating a reservation failed. Wrap the error in a UserFacingError, throw it, and stop here.
            throw new CheckoutError(
                CheckoutStage.RESERVE_ITEMS,
                err.message || 'Failed to create reservation.',
                err,
            );
        } finally {
            this.clearLoadingMessage();
        }
    }

    /**
     * Validate the current cart by creating a dry-run order.
     *
     * @returns {Promise}
     */
    async validateOrder() {
        if (this.cartService.currentCart.isEditingOrder) {
            this.log.debug('Skipping validation for order edit.');
            return;
        }

        if (!this.connectivityChecker.isOnline()) {
            this.log.debug('Skipping validation because the device is offline.');
            return;
        }

        if (!this.cartService.currentCart.reservation?.token) {
            this.log.debug('Skipping validation because no reservation was created.');
            return;
        }

        try {
            this.log.debug('Validating order.');
            this.setLoadingMessage(CheckoutStage.VALIDATE_ORDER);

            const orderConfirmationStructure = this.cartToConfirmationStructureMapper.map(this.cartService.currentCart);
            const orderResponse = await this.ordersApi.dryRunCreateOrder(orderConfirmationStructure);

            this.log.debug('Successfully validated order.', orderResponse);
        } catch (err) {
            this.log.error('Validating order failed.', err);

            // Validating the order failed. Wrap the error in a UserFacingError, throw it,and stop here.
            throw new CheckoutError(
                CheckoutStage.VALIDATE_ORDER,
                err.message || 'Order validation failed.',
                err,
            );
        } finally {
            this.clearLoadingMessage();
        }
    }

    /**
     * If there is a non-zero balance, ensure a payment method is available.
     *
     * @returns {Promise}
     */
    async ensurePaymentMethodAvailable() {
        const remainingBalance = this.cartService.getRemainingBalance();

        // Ensure we have a payment method available.
        if (remainingBalance > 0 && !this.paymentMethodService.methodsAreAvailable()) {
            // There are no payment methods available.
            throw new CheckoutError(
                CheckoutStage.TAKE_PAYMENT,
                this.langService.getText('CHECKOUT_ERROR_MESSAGES.NO_PAYMENT_METHODS_AVAILABLE'),
            );
        } else if (remainingBalance < 0 && !this.paymentMethodService.refundMethodsAreAvailable()) {
            // There are no refund payment methods available.
            throw new CheckoutError(
                CheckoutStage.TAKE_PAYMENT,
                this.langService.getText('CHECKOUT_ERROR_MESSAGES.NO_REFUND_PAYMENT_METHODS_AVAILABLE'),
            );
        }
    }

    /**
     * Show the payment modal.
     * The modal itself will handle adding payments to the cart.
     *
     * @param {string} checkoutType
     *
     * @returns {Promise}
     */
    async takePayment(checkoutType) {
        const remainingBalance = this.cartService.getRemainingBalance();
        if (remainingBalance === 0) {
            this.log.debug('Skipping payment because the remaining balance is zero.');
            return;
        }

        this.customerScreenDataService.setStage(CustomerScreenStages.PAY);
        await this.orderInProgressStasher.setState('pay');

        this.log.debug('Showing payment modal.');

        // Wrap the old Angular modal open call in a promise so, we can await it.
        const showPaymentModalPromise = new Promise((resolve, reject) => {
            // noinspection JSUnusedGlobalSymbols
            this.modalFactory
                .open(
                    'payment',
                    PaymentCtrl,
                    'partials/modals/payment/index.html',
                    {
                        checkoutType: () => checkoutType,
                    },
                )
                .then(
                    (res) => {
                        this.log.debug('Finished taking payment.');
                        resolve(res);
                    },
                    (err) => {
                        if (err === 'cancel') {
                            this.log.debug('User cancelled payment.');
                            reject(new UserCancelledPaymentError());
                        } else {
                            this.log.error('Payment modal rejected with an unexpected error', err);
                            reject(new CheckoutError(CheckoutStage.TAKE_PAYMENT, err.message));
                        }
                    },
                );
        });

        // Don't catch any errors here, let the caller handle them.
        await showPaymentModalPromise;
    }

    /**
     * @returns {Promise<OrderAdjustmentResponse>}
     */
    async createAdjustmentOrder() {
        try {
            this.log.debug('Confirming order adjustment.');
            this.setLoadingMessage(CheckoutStage.CONFIRM_ADJUSTMENT);

            const orderAdjustmentResponse = await this.orderAdjustmentConfirmer.confirmAdjustment(
                this.cartService.currentCart,
            );

            // Copy over the exceeded VAT limit flag from the original order.
            if (this.cartService.currentCart.originalOrder?.exceededVatLimit) {
                orderAdjustmentResponse.adjustedOrder.exceededVatLimit = true;
                orderAdjustmentResponse.adjustmentOrder.exceededVatLimit = true;
            }

            this.log.debug('Successfully saved order adjustment.', orderAdjustmentResponse);

            return orderAdjustmentResponse;
        } catch (err) {
            this.log.error('Error confirming adjustment.', err);
            // Don't do any error handling here anymore. It's handled in checkout, with the order error modal.
            // Simply re-throw the error now that it's been logged.
            throw err;
        } finally {
            this.clearLoadingMessage();
        }
    }

    /**
     * @returns {Promise<number>}
     */
    async createNewUnconfirmedOrderOnline() {
        if (this.cartService.currentCart.unconfirmedOrderID) {
            // The cart already contains an unconfirmed order. Return that instead of creating another one.
            this.log.debug(
                'Skipping creating unconfirmed order because one already exists.',
                this.cartService.currentCart.unconfirmedOrderID,
            );
            return this.cartService.currentCart.unconfirmedOrderID;
        }

        try {
            this.log.debug('Creating unconfirmed order.');
            this.setLoadingMessage(CheckoutStage.CREATE_UNCONFIRMED_ORDER);

            let orderConfirmationStructure = this.cartToConfirmationStructureMapper.map(this.cartService.currentCart);

            const response = await this.ordersApi.createUnconfirmedOrder(orderConfirmationStructure);

            // The OrdersApi class has already checked the response was successful and contains an order.
            const order = this.cartToOrderMapper.buildOrderFromApiConfirmation(
                this.cartService,
                response,
            );

            this.log.debug('Successfully created unconfirmed order.', order);

            // Set the order ID on the cart.
            // We can't set the whole order here because the create order endpoint doesn't return the full order.
            await this.cartService.setUnconfirmedOrder(order.ID);

            return order.ID;
        } catch (err) {
            this.log.error('Error creating unconfirmed order.', err);

            // This is not as big as problem as if we were creating a confirmed order after taking payment.
            // This may be a validation error, or a temporary problem with the server.
            // Either way, we'll tell the user about it, and they can fix it or abort before taking payment.
            throw new CheckoutError(
                CheckoutStage.CREATE_UNCONFIRMED_ORDER,
                err.message || 'Failed to create order.',
                err,
            );
        } finally {
            this.clearLoadingMessage();
        }
    }

    /**
     * Save all new payments from the current cart against the given orderID.
     * If there are any payments to save it will send them to the server, which will return all the
     * payments for that order. If there are no new payments to save we make no request so allPayments will be null.
     *
     * @param {Payment[]} payments
     * @param {Order} order
     *
     * @returns {Promise<Payment[]|null>} Returns all payments for the order after saving the new ones.
     */
    async addPaymentsToOrder(
        payments,
        order,
    ) {
        try {
            // Filter out non-new payments. (Existing payments will have an ID.)
            payments = payments.filter((p) => !p.ID);

            // Filter out zero amount payments.
            payments = this.paymentService.cleanPayments(payments);

            if (payments.length < 1) {
                // There are no new payments to save.
                this.log.debug('Skipping saving payments because there are no new payments in the cart.');

                // Normally we'd make a request to the server to save payments and that would return the complete
                // list of payments for the order. But in this case we have no payments to send so have no response.
                // Return null to indicate that we didn't get the list of payments back.
                return null;
            }

            this.log.debug('Saving payments.');
            this.setLoadingMessage(CheckoutStage.SAVE_PAYMENTS);

            // This can throw a PaymentSaveError.
            const allPayments = await this.paymentService.storePaymentsForOrder(
                order.ID,
                payments
            );

            this.log.debug(`Successfully saved ${payments.length} payment(s).`);

            return allPayments;
        } catch (err) {
            this.log.error('Error saving payments.', err);

            if (err instanceof PaymentSaveError) {
                err.setOrder(order);
                throw err;
            } else {
                // Throw an error that will be displayed to the user.
                throw new CheckoutError(
                    CheckoutStage.SAVE_PAYMENTS,
                    err.message || 'Failed to save payments for order.',
                    err,
                );
            }
        } finally {
            this.clearLoadingMessage();
        }
    }

    async confirmOrder() {
        if (!this.cartService.currentCart.unconfirmedOrderID) {
            throw new CheckoutError(
                CheckoutStage.CONFIRM_ORDER,
                'There is no unconfirmed order to confirm.',
            );
        }

        try {
            this.log.debug('Confirming order.');

            const response = await this.orderConfirmer.confirm(
                this.cartService.currentCart.unconfirmedOrderID
            );

            // The OrdersApi class has already checked the response was successful and contains an order.
            const order = this.cartToOrderMapper.buildOrderFromApiConfirmation(
                this.cartService,
                response,
            );

            this.displayAutoRedemptionWarnings(response);

            this.log.debug('Order confirmed successfully.', order);

            return order;
        } catch (err) {
            this.log.error('Error confirming order.', err);
            // Don't do any error handling here anymore. It's handled in checkout, with the order error modal.
            // Simply re-throw the error now that it's been logged.
            throw err;
        }
    }

    /**
     * @returns {Promise}
     */
    async cancelUnconfirmedOrder() {
        if (!this.cartService.currentCart.unconfirmedOrderID) {
            this.log.debug('Skipping cancelling because there is no unconfirmed order.');
            return;
        }

        try {
            this.log.debug('Cancelling unconfirmed order.');
            this.setLoadingMessage(CheckoutStage.CANCEL_UNCONFIRMED_ORDER);

            const response = await this.ordersApi.cancelOrder(this.cartService.currentCart.unconfirmedOrderID);
            this.log.debug('Cancel order response', response);

            // Clear the unconfirmed order ID from the cart.
            await this.cartService.setUnconfirmedOrder(null);

            this.log.debug('Order cancelled successfully.');
        } catch (err) {
            this.log.error('Error cancelling order.', err);

            // Throw an error that will be displayed to the user.
            throw new CheckoutError(
                CheckoutStage.CANCEL_UNCONFIRMED_ORDER,
                err.message || 'Failed to cancel order.',
                err,
            );
        } finally {
            this.clearLoadingMessage();
        }
    }

    /**
     * @returns {Promise<Order>}
     */
    async createNewConfirmedOrderOnline() {
        if (
            this.cartService.currentCart.reservation
            && this.cartService.currentCart.reservation.expiresAt <= new Date()
        ) {
            this.log.debug('Reservation has expired.');
            // The reservation has expired. Recreate it.
            this.setLoadingMessage(CheckoutStage.RESERVE_ITEMS);
            await this.createReservation();
            this.clearLoadingMessage();
        }

        const orderConfirmationStructure = this.cartToConfirmationStructureMapper.map(this.cartService.currentCart);

        try {
            this.log.debug('Creating confirmed order.');
            this.setLoadingMessage(CheckoutStage.CREATE_CONFIRMED_ORDER);

            const response = await this.ordersApi.createConfirmedOrder(orderConfirmationStructure);

            // The OrdersApi class has already checked the response was successful and contains an order.
            const order = this.cartToOrderMapper.buildOrderFromApiConfirmation(
                this.cartService,
                response,
            );

            this.displayAutoRedemptionWarnings(response);

            this.log.debug('Successfully created confirmed order.', order);

            return order;
        } catch (err) {
            this.log.log('Error creating confirmed order.', err);
            // Don't do any error handling here anymore. It's handled in checkout, with the order error modal.
            // Simply re-throw the error now that it's been logged.
            throw err;
        } finally {
            this.clearLoadingMessage();
        }
    }

    /**
     * @private
     *
     * @param {Error} err
     * @param {OrderConfirmationStructure} orderConfirmationStructure
     * @returns {string}
     */
    buildNonRecoverableOrderCreationErrorMessage(err, orderConfirmationStructure) {
        let errorText = 'The server returned an error for this order.';

        let sumPayments = 0;
        // FIXME: The confirmation structure no longer contains the payments so this error doesn't work.
        if (orderConfirmationStructure.payments?.length) {
            sumPayments = orderConfirmationStructure.payments.reduce((total, p) => total + p.amount, 0);
            if (sumPayments > 0) {
                errorText += '<br/>Please refund the customer for payments taken totalling ' + currencyFilter(Math.abs(sumPayments)) + '.';
            }
            if (sumPayments === 0) {
                errorText += '<br/>No payments were submitted to the server when attempting to save this order.';
            }
            if (sumPayments < 0) {
                errorText += '<br/>A refund of ' + currencyFilter(Math.abs(sumPayments)) + ' has been processed.';
            }
        }

        return `${errorText}<br/><br/><p><b>Error Message:</b><br/>${err.message}</p>`;
    }

    /**
     * @returns {Promise<Order>}
     */
    async createOrderFromCartForOffline() {
        return this.cartToOrderMapper.buildOrderFromApiConfirmation(
            this.cartService,
            null,
        );
    }

    /**
     * Handle saving an order while offline.
     * This now handles the standard (confirmed) order flow, and unconfirmed orders that experienced network errors.
     * For the latter, we queue up a task to add payments to the order and confirm it when we're online.
     * For the former, it puts the order into the order queue and creates the order once online.
     *
     * @returns {Promise<OnlineTask>}
     */
    async storeOrderForLaterProcessing() {
        // If an order has already been created (likely the unconfirmed order flow) then queue a task to
        // confirm the order and add payments to it instead of creating a new order.
        if (this.cartService.currentCart.unconfirmedOrderID) {
            this.log.log('Confirming unconfirmed order and adding payments offline.');

            const payments = this.paymentService.getAllNewPaymentsFromCart(this.cartService.currentCart);

            // The promises from these calls resolve once the task is added to the queue.
            // The add payments and confirm order tasks may execute out of order and make their API requests
            // out of order. That doesn't matter. We can store payments against an unconfirmed order, and vice versa.
            return Promise.all([
                // Save the payments against the order (via the queue).
                this.orderService.addPayments(
                    this.cartService.currentCart.unconfirmedOrderID,
                    payments
                ),

                // Mark the order as confirmed (via the queue).
                this.orderService.confirmOrder(
                    this.cartService.currentCart.unconfirmedOrderID
                ),
            ]);
        }

        this.log.log('Saving confirmed order offline.');

        const confirmationStructure = this.cartToConfirmationStructureMapper.map(this.cartService.currentCart);
        confirmationStructure.requestAutoRedeem = 1;

        // Payments are no longer part of the confirmation structure so build those separately.
        // TODO: https://digitickets.atlassian.net/browse/PROP-2503
        // They actually are included again since PROP-2501 and PROP-2502. But keep this redundant code in
        // for now as some day they will not be.
        // Note that here we are using API formatted payments not raw payments, because this is the kind
        // required by the offline order handling.
        const payments = this.paymentService.convertPaymentsToApiStructure(
            this.paymentService.getAllNewPaymentsFromCart(this.cartService.currentCart)
        );

        const reservationStructure = this.cartToReservationStructureMapper.map(this.cartService.currentCart);
        reservationStructure.force = 1;

        let request = new OrderRequest(
            confirmationStructure,
            payments,
            reservationStructure,
        );

        this.log.debug('Storing order for later processing.', request);

        return this.orderQueue.addRequest(request, OnlineTaskTrigger.OFFLINE);
    }

    /**
     * @private
     *
     * @param {OrderResponse} orderResponse
     */
    displayAutoRedemptionWarnings(orderResponse) {
        if (!orderResponse?.autoLineRedemptionsResult) {
            return;
        }

        for (let line of Object.values(orderResponse.autoLineRedemptionsResult.lines)) {
            if (line.errorType && line.errorType === 'OutstandingWaiversException') {
                this.toastFactory.warning(
                    'WAIVERS.NOT_AUTO_REDEEMED_TOAST__TITLE',
                    this.langService.getText('WAIVERS.NOT_AUTO_REDEEMED_TOAST__MESSAGE'),
                );
                break;
            }
        }
    }

    /**
     * @private
     *
     * In case a memberships' details were changed while they in the cart whilst being renewed,
     * we need to persist those changes to the memberships' data. The API does not do this automatically.
     *
     * @returns {Promise}
     */
    async updateRenewingMembershipsData() {
        // The API does not update members' data upon renewal so this will manually update the members after a
        // successful renewal. It uses the same endpoint and method as the Account page.
        const hasRenewal = this.cartService.currentCart.containsMembershipRenewal();
        if (hasRenewal) {
            this.log.debug('Updating renewing memberships data.');

            const cartItems = this.cartService.currentCart.getItems();
            for (let i = 0; i < cartItems.length; i++) {
                const item = cartItems[i];
                if (item.renewingMembership) {
                    let dataset = item.membershipDatasets[0];
                    this.membershipService.updateMembership(
                        item.renewingMembership,
                        dataset,
                    );
                }
            }
        }
    }

    /**
     * @returns {boolean}
     */
    useUnconfirmedOrderFlow() {
        return !!this.userService.currentUser?.company?.settings?.enablePropointUnconfirmedOrderFlow;
    }

    /**
     * @param {string} stage One of CheckoutStage.
     */
    setLoadingMessage(stage) {
        setLoadingMessage('CHECKOUT_PROCESSING_MESSAGES.' + stage);
    }

    clearLoadingMessage() {
        clearLoadingMessage();
    }

    /**
     * @param {string} checkoutStage
     * @param {Cart} cart
     * @param {Error|ApiError} error
     *
     * @returns {Promise<number|null>} Returns the ID of the logged error if available.
     */
    async logOrderError(
        checkoutStage,
        cart,
        error
    ) {
        if (error instanceof PaymentSaveError && error.paymentError) {
            // Use the underlying error from within the PaymentSaveError.
            error = error.paymentError;
        }

        return this.apiClient
            .request(
                'POST',
                'propoint-order-errors',
                {
                    checkoutStage,
                    thirdPartyRef: cart.thirdPartyRef,
                    errorMessage: error.message,
                    error: JSON.stringify({
                        name: error.name,
                        message: error.message,
                        stack: error.stack,
                    }),
                    request: error.requestConfig ? JSON.stringify(sanitizeLoggedObject({
                        method: error.requestConfig.method,
                        url: error.requestConfig.url,
                        params: error.requestConfig.params,
                        headers: error.requestConfig.headers,
                        data: typeof error.requestConfig.data === 'string' ? qs.parse(error.requestConfig.data) : error.requestConfig.data,
                    })) : null,
                    responseCode: error.response?.status || null,
                    response: error.responseData ? JSON.stringify(error.responseData) : null,
                },
            )
            // Ignore responses and errors from the logging.
            .then((res) => {
                return res?.data?.propointOrderErrorID || null;
            })
            .catch(() => {
                return null;
            });
    }

    /**
     * Display an error modal to the user to show when saving their order failed.
     *
     * The order parameter can be built from the cart alone without the API response, the same way we
     * print a receipt for an offline order.
     *
     * @param {Cart} cart
     *
     * @return {Promise<OrderErrorModalResult>} Resolves when the modal is closed.
     */
    async showOrderErrorModal(cart) {
        return this.vueModalFactory
            .open(
                MODAL_NAMES__EPOS.ORDER_ERROR,
                {
                    cart,
                    // TODO: https://digitickets.atlassian.net/browse/PROP-540
                    cartService: this.cartService,
                },
            );
    }

    /**
     * @param {Cart} cart
     * @param {Order} order
     *
     * @return {Promise<PaymentErrorModalResult>} Resolves when the modal is closed.
     */
    async showPaymentErrorModal(cart, order) {
        return this.vueModalFactory
            .open(
                MODAL_NAMES__EPOS.PAYMENT_ERROR,
                {
                    cart,
                    order,
                }
            );
    }

    /**
     * @param {Cart} cart
     * @param {Order?} order
     *
     * @returns {Promise}
     */
    async displayErrorModals(cart, order) {
        // Close any existing modals.
        this.closeErrorModals();

        let clearCart = false;
        let retry = false;
        /**
         * @type {CheckoutOptions}
         */
        let retryOptions = {};

        const hasOrderErrors = cart.hasUnresolvedOrderSaveErrors();
        const hasPaymentErrors = cart.hasUnresolvedPaymentSaveErrors();

        // Now, after saving payments, deal with any order error that may have occurred earlier.
        // Show the order error modal if there are *any* unresolved order errors, not necessarily from this attempt.
        if (hasOrderErrors) {
            // Show the order error modal.
            const orderErrorResult = await this.showOrderErrorModal(this.cartService.currentCart);
            switch (orderErrorResult?.action) {
                case CheckoutErrorActions.ABANDON:
                    await this.cartService.resolveOrderSaveErrors();
                    clearCart = true;
                    break;

                case CheckoutErrorActions.CONTINUE_TO_PAYMENT_ERRORS:
                    // Do nothing, just continue to the payment error modal.
                    break;

                case CheckoutErrorActions.RETRY:
                    retry = true;
                    break;

                case CheckoutErrorActions.STORE_FOR_LATER:
                    // To store the order for later, we'll run the checkout flow again but force it
                    // into offline mode so the order always goes into the queue. Set the skip payment
                    // flag too so the payment modal doesn't show again if there is an outstanding balance.
                    // This allows the change modal to show once the order has been saved in the queue.
                    retry = true;
                    retryOptions = {
                        skipPayment: true,
                        forceOffline: true,
                    };
                    await this.cartService.resolveOrderSaveErrors();
                    break;

                case CheckoutErrorActions.VIEW_ORDER:
                    await this.cartService.resolveOrderSaveErrors();
                    clearCart = true;
                    // The modal handles navigating to the order details page.
                    break;
            }
        }

        // Show the payment error modal if there are *any* unresolved payment errors, not necessarily from this attempt.
        if (hasPaymentErrors) {
            // Show the payment error modal.
            const paymentErrorResult = await this.showPaymentErrorModal(this.cartService.currentCart, order);
            switch (paymentErrorResult?.action) {
                case 'abandon':
                    await this.cartService.resolvePaymentSaveErrors();
                    clearCart = true;
                    // The modal handles navigating to the order details page.
                    break;

                case 'retry':
                    retry = true;
                    break;

                case 'viewOrder':
                    await this.cartService.resolvePaymentSaveErrors();
                    clearCart = true;
                    // The modal handles navigating to the order details page.
                    break;
            }
        }

        if (retry) {
            console.log('Retrying checkout after user requested retry from error modal.');

            // The user requested to retry, either from the order error modal or the payment error modal.
            // In a timeout so that the errors can be thrown below to stop execution of this checkout request
            // before we try again.
            setTimeout(async () => {
                await container.cartService.modifyCurrentCart((currentCart) => {
                    currentCart.receiptPrintedAt = null;
                });

                // To retry, simply tell SellCtrl to click the Pay button again.
                // If the payment has already been taken the payment modal won't show again.
                // If a partial payment or no payment was taken, the user will have to re-confirm that.
                // If an order has already been created, it won't create a new one.
                // A new reservation won't be made because the previous reservation should still be around.
                // But if we know that that has expired it will automatically create a new reservation.
                container.eventBus.broadcast(EventNames.PAY_REQUESTED, retryOptions);
            }, 500);
        } else if (clearCart) {
            console.log('Clearing cart after user requested to abandon/ignore from error modal.');

            // The user requested to abandon the order, either from the order error modal or the payment error modal.
            // Clear the cart so they can start again with a new order.
            await this.cartService.clear();
        }
    }

    closeErrorModals() {
        this.vueModalFactory.closeAllOfType(MODAL_NAMES__EPOS.ORDER_ERROR);
        this.vueModalFactory.closeAllOfType(MODAL_NAMES__EPOS.PAYMENT_ERROR);
    }

    /**
     * When testing should we skip the connectivity test after taking a payment?
     * Triggered by adding a custom note to the order.
     *
     * @see https://digitickets.atlassian.net/wiki/x/DABv9w
     *
     * @returns {boolean}
     */
    shouldSkipConnectivityCheckAfterPayment() {
        return !!this.cartService.currentCart?.notes?.includes('skip-connectivity-check-after-payment');
    }
}

module.exports = CheckoutService;
